import { Component, Color, Vector, Sprite, Actor } from "excalibur";

interface HighlightOptions {
    /** Color to use for the highlight effect (when using tint method) */
    color?: Color;
    /** Scale factor for the highlight effect (when using scale method) */
    scale?: number;
    /** Which highlight method to use: 'tint', 'scale', or 'both' */
    method?: "tint" | "scale" | "both";
}

/**
 * Component that adds mouse hover highlight functionality to any Actor
 */
class HighlightComponent extends Component {
    /** The color to use for highlighting (when using tint method) */
    private readonly highlightColor: Color;
    /** The scale factor to use for highlighting (when using scale method) */
    private readonly highlightScale: number;
    /** Which highlight method(s) to use */
    private readonly highlightMethod: "tint" | "scale" | "both";

    /** Reference to the owner Actor */
    private actor: Actor | null = null;
    /** Original scale of the Actor before highlighting */
    private _originalScale: Vector = Vector.One;
    /** Original tint of the Actor's sprite before highlighting */
    private _originalTint: Color = Color.White;
    /** Whether the highlight effect is currently active */
    private _isHighlighted: boolean = false;

    /**
     * Creates a new HighlightComponent
     * @param options - Configuration options for the highlight effect
     */
    constructor(options: HighlightOptions = {}) {
        super();
        this.highlightColor = options.color || new Color(255, 255, 0, 0.8); // Default yellow highlight
        this.highlightScale = options.scale || 1.1;
        this.highlightMethod = options.method || "tint"; // 'tint', 'scale', or 'both'
    }

    /**
     * Called when the component is added to an Actor
     * @param owner - The Actor this component is attached to
     */
    public onAdd(owner: Actor): void {
        // Store the actor for later use
        this.actor = owner;

        // Save original properties
        this._originalScale = owner.scale.clone();

        // Set up mouse events
        //owner.enableCapturePointer = true;
        owner.on("pointerenter", () => this.highlight());
        owner.on("pointerleave", () => this.unhighlight());
    }

    /**
     * Apply the highlight effect to the Actor
     */
    public highlight(): void {
        if (!this.actor || this._isHighlighted) return;
        this._isHighlighted = true;

        // Apply highlight based on selected method
        if (this.highlightMethod === "tint" || this.highlightMethod === "both") {
            if (this.actor.graphics.current instanceof Sprite) {
                // Save original tint
                this._originalTint =
                    this.actor.graphics.current.tint?.clone() ?? this._originalTint;
                // Apply highlight tint
                this.actor.graphics.current.tint = this.highlightColor;
            }
        }

        if (this.highlightMethod === "scale" || this.highlightMethod === "both") {
            // Save original scale if not already saved
            this._originalScale = this.actor.scale.clone();
            // Apply highlight scale
            this.actor.scale = this._originalScale.scale(this.highlightScale);
        }
    }

    /**
     * Remove the highlight effect from the Actor
     */
    public unhighlight(): void {
        if (!this.actor || !this._isHighlighted) return;
        this._isHighlighted = false;

        // Restore original properties
        if (this.highlightMethod === "tint" || this.highlightMethod === "both") {
            if (this.actor.graphics.current instanceof Sprite) {
                this.actor.graphics.current.tint = this._originalTint;
            }
        }

        if (this.highlightMethod === "scale" || this.highlightMethod === "both") {
            this.actor.scale = this._originalScale.clone();
        }
    }

    /**
     * Check if the highlight effect is currently active
     */
    public isHighlighted(): boolean {
        return this._isHighlighted;
    }
}

export { HighlightComponent, HighlightOptions };

// Usage example
/*
const character = new Actor({
  // Your character configuration
});

// Add the highlight component to your character
character.addComponent(new HighlightComponent({
  color: new Color(0, 255, 255, 0.8), // Cyan highlight
  scale: 1.15,
  method: 'both' // Use both tint and scale for highlight
}));

game.add(character);
*/
